import { isEqual } from "lodash-es";
import { useEffect, useRef } from "react";
import { type RouteComponentProps } from "react-router";

import { routes } from "utilities/routes";

/**
 * Keeps the Submittal detail route in step with the left-nav JobPicker.
 *
 * The route is `/Submittal/:id/:jobId`, so the submittal on screen is pinned to a job by the URL
 * rather than the picker. A submittal belongs to exactly one job, so when the picker moves off that
 * job there is no equivalent submittal to show — the page falls back to the list, which honors the
 * new selection.
 *
 * This replaces the route's old `redirectOnJobChangeURL`, which `ListPageLayout` implements as a
 * `window.location.assign` — a full page reload that throws away the SPA. Navigating through
 * history keeps the app mounted and lets any unsaved-changes guard (built on `history.block`)
 * prompt normally instead of the browser's generic unload dialog.
 *
 * The ref guards the initial render, so a deep link whose stored picker state differs from the URL
 * jobId does not trigger a false redirect. Empty `jobIds` is skipped: it is the AllJobs→NoJobs
 * fallback the picker emits transiently while reloading, not a user selection.
 *
 * `isAllJobsSelected` is read alongside `jobIds` because a builder with a single job expands
 * "All Jobs" to that one id — indistinguishable from picking the job itself by `jobIds` alone.
 */
export const useSubmittalJobRedirect = (
    jobIds: number[],
    isAllJobsSelected: boolean,
    urlJobId: number,
    history: RouteComponentProps["history"],
) => {
    const previousSelectionRef = useRef<{ jobIds: number[]; isAllJobsSelected: boolean }>();

    useEffect(() => {
        const previous = previousSelectionRef.current;
        // Record the selection before deciding, so a stale `previous` can't redirect twice.
        previousSelectionRef.current = { jobIds, isAllJobsSelected };

        if (previous === undefined) {
            return;
        }
        if (isEqual(previous.jobIds, jobIds) && previous.isAllJobsSelected === isAllJobsSelected) {
            return;
        }
        if (jobIds.length === 0) {
            return;
        }
        // The submittal on screen still belongs to a selected job, so there is nothing to leave.
        if (!isAllJobsSelected && jobIds.length === 1 && jobIds[0] === urlJobId) {
            return;
        }

        history.replace(routes.submittal.getListLink());
    }, [jobIds, isAllJobsSelected, urlJobId, history]);
};
